/**
 * The /how-it-works story: five chapters, each of one to three beats. A beat
 * is one scene on the stage: the desk calendar (member side) or the planner's
 * screen (lead side), the characters' marks, and the real Harbourline shots
 * that hover beside it. Positions are percentages of the stage: x is the feet
 * centre from the left, y the feet from the bottom.
 */

export type ActorId = "maya" | "jo" | "alex" | "sam";
/** Poses in the Tarii asset kit (`/site/characters/<name>/<pose>.svg`). */
export type Pose = "idle" | "point" | "phone" | "place" | "celebrate";

export interface Mark {
  x: number;
  y: number;
  pose?: Pose;
  say?: string;
  /** Off stage: the actor walks out to x and fades. */
  out?: boolean;
  /** Behind the planner's desk: the desk hides the legs. */
  seated?: boolean;
  /** Lift the speech bubble a row higher, clear of a neighbour's bubble. */
  sayHigh?: boolean;
}

export interface StageShot {
  src: string;
  alt: string;
  width: number;
  height: number;
}

export interface Float extends StageShot {
  kind: "laptop" | "phone";
  x: number;
  y: number;
  /** Width in % of the stage. */
  w: number;
}

export interface Scene {
  mode: "calendar" | "screen";
  label: string;
  screen?: StageShot;
  actors: Record<ActorId, Mark>;
  /** Who walks in first, one at a time. Default: the on-stage actors in ACTORS order. */
  order?: ActorId[];
  floats: Float[];
  /** Split screen: the planner's monitor on the left, this member's phone on the right. */
  split?: StageShot;
  /** A cover swap the visitor makes: who leaves the seat on `day`, who takes it. */
  swap?: { day: number; out: ActorId; in: ActorId };
  /** The wider team: silhouettes pass behind the calendar and add requests. */
  crowd?: boolean;
}

export const ACTORS: ReadonlyArray<{ id: ActorId; name: string; role: string }> = [
  { id: "maya", name: "Maya", role: "Team member" },
  { id: "jo", name: "Jo", role: "Team member" },
  { id: "alex", name: "Alex", role: "Team member" },
  { id: "sam", name: "Sam", role: "Planner" },
];

// Planner screens are full 1100x825 browser windows from the Harbourline demo
// team (#2490 journey captures), so they fill the 4:3 monitor exactly. Phone
// shots are 780x1688. A few shots from the first site round stay.
const J = "/site/harbourline/story/";
const H = "/site/harbourline/";
const lead = (file: string, alt: string): StageShot => ({
  src: `${J}${file}.webp`,
  alt,
  width: 1100,
  height: 825,
});
const laptop = (s: StageShot, x: number, y: number, w = 24): Float => ({
  ...s,
  kind: "laptop",
  x,
  y,
  w,
});
const phone = (src: string, alt: string, x: number, y: number, w = 13): Float => ({
  src,
  alt,
  width: 780,
  height: 1688,
  kind: "phone",
  x,
  y,
  w,
});

const OUT_LEFT: Mark = { x: -14, y: 6, out: true };
const OUT_RIGHT: Mark = { x: 114, y: 6, out: true };
// The planner stands under the caption, clear of the monitor.
const AT_DESK: Mark = { x: -24, y: 2 };
const AWAY = { maya: OUT_LEFT, jo: OUT_RIGHT, alex: OUT_RIGHT };

/** The beats in story order. CHAPTERS groups them; `cardsAt` keys on this index. */
export const SCENES: ReadonlyArray<Scene> = [
  // 0. Set up the month: stations.
  {
    mode: "screen",
    label: "Planner",
    screen: lead(
      "g-03-stations",
      "Stations page: the need per day for each station and the days each one runs.",
    ),
    actors: {
      ...AWAY,
      sam: { ...AT_DESK, pose: "point", say: "Control room: three a day." },
    },
    floats: [
      laptop(
        lead(
          "g-04-people",
          "One person's card on the People page with the stations this person may work.",
        ),
        73,
        4,
      ),
    ],
  },
  // 1. Set up the month: skills and rules.
  {
    mode: "screen",
    label: "Planner",
    screen: lead(
      "g-01-rules",
      "Rules and fairness page with the hard rules and a switch for each one.",
    ),
    actors: { ...AWAY, sam: { ...AT_DESK, say: "Rest after each call: on." } },
    floats: [
      laptop(
        lead(
          "g-05-people-on",
          "The same person's card after the planner ticks one more station they may work.",
        ),
        73,
        4,
      ),
    ],
  },
  // 2. Set up the month: the team sends requests.
  {
    mode: "calendar",
    label: "Team members",
    actors: {
      maya: { x: -30, y: 4, pose: "phone", say: "Leave on the 20th and 21st, please." },
      jo: { x: 88, y: 6, pose: "place", say: "Can I have the 8th off?" },
      alex: { x: -8, y: 0, pose: "point", say: "Late shifts suit me.", sayHigh: true },
      sam: OUT_RIGHT,
    },
    crowd: true,
    floats: [
      phone(`${J}x-04-days-20-21.webp`, "Phone leave form with 20 and 21 October picked.", 2, 8),
      laptop(
        lead(
          "q-01-queue-new",
          "The planner's request queue with the new leave request and Accept into plan, Ask to move and Decline.",
        ),
        75,
        8,
      ),
    ],
  },
  // 3. The roster: Sam starts a solve.
  {
    mode: "screen",
    label: "Planner",
    screen: lead("q-04-solving", "The solve console with the dates to solve, 12 to 25 October."),
    actors: { ...AWAY, sam: { ...AT_DESK, say: "These dates. Solve." } },
    floats: [],
  },
  // 4. The roster: the shifts land around the requests.
  {
    mode: "calendar",
    label: "Team members",
    order: ["maya", "jo"],
    actors: {
      maya: { x: -30, y: 4, pose: "point", say: "My 20th and 21st stay free." },
      jo: { x: 88, y: 6, pose: "celebrate", say: "The 8th is still off." },
      alex: OUT_RIGHT,
      sam: OUT_RIGHT,
    },
    floats: [
      laptop(
        lead(
          "q-05-solved",
          "The roster solved for 12 to 25 October, not yet published, with Publish roster.",
        ),
        75,
        8,
      ),
    ],
  },
  // 5. The roster: a change by hand is checked.
  {
    mode: "screen",
    label: "Planner",
    screen: lead(
      "r-02-move",
      "Move panel open on one shift, with a teammate picked to cover it and Apply cover-change.",
    ),
    actors: {
      ...AWAY,
      sam: { ...AT_DESK, pose: "point", say: "Over the weekly cap? It says so." },
    },
    floats: [
      laptop(
        {
          src: `${H}p8-validation.png`,
          alt: "Roster validation panel listing a weekly session cap rule with the member and the dates it affects.",
          width: 1326,
          height: 315,
        },
        62,
        4,
        36,
      ),
    ],
  },
  // 6. Published: everyone.
  {
    mode: "calendar",
    label: "Everyone",
    order: ["sam", "maya", "jo", "alex"],
    actors: {
      maya: { x: -36, y: 2, pose: "celebrate", say: "It's out!" },
      jo: { x: 90, y: 4, pose: "celebrate" },
      alex: { x: 2, y: 0, pose: "phone" },
      sam: { x: -16, y: 0, pose: "point", say: "Published." },
    },
    floats: [
      laptop(
        lead(
          "q-06-published",
          "The roster after Publish roster, with the line saying who it was published to.",
        ),
        0,
        8,
        26,
      ),
      phone(`${J}x-08-inbox.webp`, "A member's inbox with the published row.", 86, 8),
    ],
  },
  // 7. Published: the planner's grid and one member's phone.
  {
    mode: "screen",
    label: "Planner and team member",
    order: ["sam", "maya"],
    screen: lead(
      "r-01-grid",
      "The solved roster grid for 12 to 25 October with one row per member.",
    ),
    actors: {
      maya: { x: 94, y: 2, pose: "phone", say: "Only my own shifts." },
      jo: OUT_RIGHT,
      alex: OUT_RIGHT,
      sam: { ...AT_DESK, pose: "point", say: "The whole team, in one grid." },
    },
    split: {
      src: `${J}x-10-calendar.webp`,
      alt: "A member's phone calendar with their own shifts and their leave on 20 and 21 October.",
      width: 780,
      height: 1688,
    },
    floats: [],
  },
  // 8. Published: Jo's inbox, Alex's Telegram.
  {
    mode: "calendar",
    label: "Team members",
    order: ["jo", "alex"],
    actors: {
      maya: OUT_LEFT,
      jo: { x: 88, y: 4, pose: "phone", say: "It's in my inbox." },
      alex: { x: -8, y: 0, pose: "phone", say: "Mine opens in Telegram.", sayHigh: true },
      sam: OUT_RIGHT,
    },
    floats: [
      phone(
        `${J}x-09-inbox-open.webp`,
        "A member's inbox row opened, with Open calendar.",
        71,
        3,
        11,
      ),
      phone(
        "/site/telegram/tg-calendar.webp",
        "The same calendar opened inside Telegram, with the month's shifts and leave.",
        85,
        3,
      ),
    ],
  },
  // 9. A sick day: Alex reports it.
  {
    mode: "calendar",
    label: "A sick day",
    order: ["alex"],
    actors: {
      maya: OUT_LEFT,
      jo: OUT_RIGHT,
      alex: { x: 88, y: 4, pose: "phone", say: "Not well. Sick on the 22nd." },
      sam: OUT_RIGHT,
    },
    floats: [phone(`${J}m2-02-sick-form.webp`, "The phone form to report a sick day.", 86, 8)],
  },
  // 10. A sick day: Maya is free, and the visitor swaps her in.
  {
    mode: "calendar",
    label: "A sick day",
    order: ["alex", "maya"],
    swap: { day: 22, out: "alex", in: "maya" },
    actors: {
      maya: { x: -14, y: 2, pose: "place", say: "I'm free on the 22nd." },
      jo: OUT_RIGHT,
      alex: { x: 88, y: 4, pose: "phone" },
      sam: OUT_RIGHT,
    },
    floats: [
      laptop(
        lead(
          "c-02-cover",
          "Shortfall options for one day: a free teammate to seat, without bending rest or cancelling leave.",
        ),
        0,
        8,
      ),
      laptop(
        lead(
          "c-03-covered",
          "After the cover is confirmed: the gap is filled and the day counts as covered.",
        ),
        0,
        34,
      ),
    ],
  },
  // 11. End of the month: hours and balance.
  {
    mode: "screen",
    label: "Planner and team member",
    order: ["sam", "maya"],
    screen: lead(
      "r-05-published",
      "The published roster with the figures for sessions, stretched days and calls for each level.",
    ),
    actors: {
      maya: { x: 94, y: 2, pose: "phone", say: "My hours against my contract." },
      jo: OUT_RIGHT,
      alex: OUT_RIGHT,
      sam: { ...AT_DESK, pose: "point", say: "Balance per level. No ranking." },
    },
    split: {
      src: `${H}p10-phone.png`,
      alt: "Phone page showing a member's own hours for these dates.",
      width: 780,
      height: 1688,
    },
    floats: [],
  },
];

/** The beat on which the roster goes out: the calendar shows Published from here on. */
export const PUBLISHED_FROM = 6;

/**
 * One block of the words: a title, a sentence per beat and an "In Tarii"
 * line. A block covers a run of beats that show the same stage asset (the
 * monitor, the calendar or the split screen), so the words change when the
 * picture does (#2996).
 */
export interface WordBlock {
  /** The URL hash, without `#`. The first block of a chapter keeps the chapter's old hash. */
  id: string;
  /** A short noun phrase. */
  title: string;
  /** One sentence per beat; `scene` is the index in SCENES. */
  beats: ReadonlyArray<{ scene: number; text: string }>;
  /** The one line under the sentences that says what the product does. */
  inTarii: string;
}

export interface Chapter {
  /** The chapter's own hash, an alias of its first block. */
  id: string;
  /** The chapter's name. */
  title: string;
  /** The blocks, in order. Each has its own dot on the rail. */
  blocks: ReadonlyArray<WordBlock>;
}

export const CHAPTERS: ReadonlyArray<Chapter> = [
  {
    id: "set-up",
    title: "Set up the month",
    blocks: [
      {
        id: "set-up",
        title: "Set up the month",
        beats: [
          {
            scene: 0,
            text: "The planner sets how many people each station needs and who may work it.",
          },
          { scene: 1, text: "Then the skills each person holds and the rules to follow." },
        ],
        inTarii: "The planner sets stations, skills and rules before the first solve.",
      },
      {
        id: "requests",
        title: "Requests from the team",
        beats: [
          {
            scene: 2,
            text: "Members ask for leave on 20 and 21 October, the 8th off, and some late shifts.",
          },
        ],
        inTarii: "Members send requests from their phone. The planner reviews them in one queue.",
      },
    ],
  },
  {
    id: "roster",
    title: "Tarii builds the roster",
    blocks: [
      {
        id: "solve",
        title: "Tarii builds the roster",
        beats: [{ scene: 3, text: "The planner picks the dates and starts a solve." }],
        inTarii: "The solve works from the setup and the chosen dates.",
      },
      {
        id: "roster-fills",
        title: "A filled roster",
        beats: [{ scene: 4, text: "Tarii fills every station around the leave and the day off." }],
        inTarii: "Requested leave and days off stay free.",
      },
      {
        id: "move-by-hand",
        title: "A move by hand",
        beats: [
          {
            scene: 5,
            text: "If a hand move breaks a rule, like the weekly session cap, Tarii names it.",
          },
        ],
        inTarii: "Every change is checked against the rules before it saves.",
      },
    ],
  },
  {
    id: "published",
    title: "Published",
    blocks: [
      {
        id: "published",
        title: "Published",
        beats: [
          { scene: 6, text: "The planner publishes the roster and everyone gets a message." },
        ],
        inTarii: "Everyone gets a message when the roster goes out.",
      },
      {
        id: "who-sees-what",
        title: "Who sees what",
        beats: [
          {
            scene: 7,
            text: "The planner sees the whole team. Members see only their own shifts.",
          },
        ],
        inTarii: "Each member sees their own shifts, and nobody else's.",
      },
      {
        id: "inbox",
        title: "In the inbox",
        beats: [{ scene: 8, text: "It arrives in each member's inbox or in Telegram." }],
        inTarii: "Each member gets the roster in their inbox or in Telegram.",
      },
    ],
  },
  {
    id: "sick-day",
    title: "A sick day",
    blocks: [
      {
        id: "sick-day",
        title: "A sick day",
        beats: [
          { scene: 9, text: "On the 22nd a member reports sick from their phone." },
          { scene: 10, text: "The seat opens. Tarii lists who is free on the 22nd. Swap one in." },
        ],
        inTarii: "Only the people a change affects are told.",
      },
    ],
  },
  {
    id: "end-of-month",
    title: "End of the month",
    blocks: [
      {
        id: "end-of-month",
        title: "End of the month",
        beats: [
          {
            scene: 11,
            text: "Members check hours against their contract. Balance shows per level, never a ranking of people.",
          },
        ],
        inTarii: "Workload shows per level, not per person.",
      },
    ],
  },
];

/** The old ten-point links (`#point-1` to `#point-10`): the chapter that now holds each point. */
const POINT_CHAPTER = [0, 0, 0, 1, 2, 2, 3, 1, 2, 4];

/** Every block of words in story order, with the index of its chapter. */
export const BLOCKS = CHAPTERS.flatMap((c, chapter) => c.blocks.map((b) => ({ ...b, chapter })));

/**
 * `#requests` gives that block. A chapter's own hash (`#roster`) and the old
 * `#point-7` give the chapter's first block. No hash or an unknown one gives 0.
 */
export function blockFromHash(hash: string): number {
  const id = hash.replace(/^#/, "");
  const at = BLOCKS.findIndex((b) => b.id === id);
  if (at >= 0) return at;
  const chapter = CHAPTERS.findIndex((c) => c.id === id);
  const n = Number(/^point-(\d+)$/.exec(id)?.[1]);
  const want = chapter >= 0 ? chapter : (POINT_CHAPTER[n - 1] ?? 0);
  return Math.max(
    0,
    BLOCKS.findIndex((b) => b.chapter === want),
  );
}

/** October 2026 starts on a Thursday: three blank cells in a Monday-first grid. */
export const MONTH_OFFSET = 3;
export const MONTH_DAYS = 31;

export type CardKind = "leave" | "off" | "pref" | "day" | "night" | "sick" | "cover";
export interface DayCard {
  day: number;
  kind: CardKind;
  /** "team": someone in the wider team, drawn as a silhouette. */
  who: ActorId | "team";
  /** The beat on which the card first lands, so it can animate in. */
  from: number;
  /** For "team" cards: the passer-by (index in GHOSTS) who adds it. */
  ghost?: number;
}

/** Passers-by in the background of the requests beat: sprite, direction, depth. */
export const GHOSTS: ReadonlyArray<{ sprite: ActorId; dir: 1 | -1; y: number }> = [
  { sprite: "jo", dir: 1, y: 22 },
  { sprite: "sam", dir: -1, y: 26 },
  { sprite: "maya", dir: 1, y: 24 },
  { sprite: "alex", dir: -1, y: 20 },
];

const REQUESTS_FROM = 2;
const SHIFTS_FROM = 4;
const SICK_FROM = 9;
const COVER_FROM = 10;

const REQUESTS: DayCard[] = [
  { day: 8, kind: "off", who: "jo", from: REQUESTS_FROM },
  { day: 20, kind: "leave", who: "maya", from: REQUESTS_FROM },
  { day: 21, kind: "leave", who: "maya", from: REQUESTS_FROM },
  { day: 23, kind: "pref", who: "alex", from: REQUESTS_FROM },
  { day: 3, kind: "off", who: "team", from: REQUESTS_FROM, ghost: 0 },
  { day: 13, kind: "leave", who: "team", from: REQUESTS_FROM, ghost: 1 },
  { day: 14, kind: "leave", who: "team", from: REQUESTS_FROM, ghost: 1 },
  { day: 27, kind: "pref", who: "team", from: REQUESTS_FROM, ghost: 2 },
  { day: 16, kind: "off", who: "team", from: REQUESTS_FROM, ghost: 3 },
  { day: 30, kind: "off", who: "team", from: REQUESTS_FROM, ghost: 3 },
];

/** Example shifts once a solve ran. An illustration, not a solver result. */
function exampleShifts(): DayCard[] {
  // day % 3 picks the person; the 22nd falls to Alex, who reports sick later.
  const who: ActorId[] = ["maya", "alex", "jo"];
  const out: DayCard[] = [];
  for (let day = 1; day <= MONTH_DAYS; day++) {
    if (REQUESTS.some((r) => r.day === day)) continue;
    const person = who[day % 3] as ActorId;
    out.push({ day, kind: day % 4 === 0 ? "night" : "day", who: person, from: SHIFTS_FROM });
  }
  return out;
}
const SHIFTS = exampleShifts();

/**
 * What the desk calendar shows at beat `step` (0-based). The calendar keeps
 * what earlier beats added: requests, then shifts, then the sick day and cover.
 */
export function cardsAt(step: number): DayCard[] {
  const cards = step >= REQUESTS_FROM ? [...REQUESTS] : [];
  if (step >= SHIFTS_FROM) cards.push(...SHIFTS);
  if (step >= SICK_FROM) {
    const i = cards.findIndex((c) => c.day === 22);
    if (i >= 0) cards.splice(i, 1);
    cards.push({ day: 22, kind: "sick", who: "alex", from: SICK_FROM });
    if (step >= COVER_FROM) cards.push({ day: 22, kind: "cover", who: "maya", from: COVER_FROM });
  }
  return cards;
}

export const CARD_LABEL: Record<CardKind, string> = {
  leave: "Leave",
  off: "Day off",
  pref: "Prefers late",
  day: "Day",
  night: "Night",
  sick: "Sick day",
  cover: "Cover",
};
